import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ONBOARDING_PROGRESS_STEPS, getProgressStepIndex } from '../steps';
import type { OnboardingStepId } from '../types';
import { OnboardingStepIcon } from './OnboardingStepper';

export type OnboardingMobileProgressProps = {
    currentStep: OnboardingStepId;
};

const ICON_SIZE = 24;
const TRACK_INSET = 10;
const LABEL_OFFSET = 4;
const LABEL_HEIGHT = 14;

function labelPosition(index: number, lastIndex: number): Record<string, number | string> {
    if (index === 0) {
        return { left: 0 };
    }
    if (index === lastIndex) {
        return { right: 0 };
    }
    return { left: '50%', transform: 'translateX(-50%)' };
}

export function OnboardingMobileProgress({ currentStep }: OnboardingMobileProgressProps): ReactElement {
    const { t } = useTranslation();
    const activeStep = getProgressStepIndex(currentStep);
    const lastIndex = ONBOARDING_PROGRESS_STEPS.length - 1;
    const fill = lastIndex > 0 ? activeStep / lastIndex : 0;

    return (
        <Box component="nav" sx={{ px: 3, pt: 2, pb: `${LABEL_OFFSET + LABEL_HEIGHT + 16}px` }}>
            <Box sx={{ position: 'relative' }}>
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        left: TRACK_INSET,
                        right: TRACK_INSET,
                        top: ICON_SIZE / 2 - 1,
                        height: 2,
                        bgcolor: 'divider',
                    }}>
                    <Box sx={{ height: '100%', width: `${fill * 100}%`, bgcolor: 'primary.main' }} />
                </Box>
                <Box
                    component="ol"
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'space-between',
                        listStyle: 'none',
                        p: 0,
                        m: 0,
                    }}>
                    {ONBOARDING_PROGRESS_STEPS.map((step, index) => {
                        const label = t(step.labelKey);
                        const active = index === activeStep;
                        const completed = index < activeStep;
                        const icon = <OnboardingStepIcon active={active} completed={completed} />;
                        return (
                            <Box
                                component="li"
                                key={step.id}
                                aria-current={active ? 'step' : undefined}
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    width: ICON_SIZE,
                                    height: ICON_SIZE,
                                    borderRadius: '50%',
                                    bgcolor: 'background.paper',
                                }}>
                                {completed ? (
                                    <Box
                                        component={Link}
                                        to={step.path}
                                        aria-label={label}
                                        sx={{
                                            display: 'flex',
                                            borderRadius: '50%',
                                            '&::after': { content: '""', position: 'absolute', inset: -6 },
                                        }}>
                                        {icon}
                                    </Box>
                                ) : (
                                    <Box component="span" aria-label={label} role="img" sx={{ display: 'flex' }}>
                                        {icon}
                                    </Box>
                                )}
                                {active && (
                                    <Typography
                                        aria-hidden
                                        sx={{
                                            position: 'absolute',
                                            top: ICON_SIZE + LABEL_OFFSET,
                                            ...labelPosition(index, lastIndex),
                                            color: 'primary.main',
                                            fontSize: 10,
                                            fontWeight: 500,
                                            lineHeight: `${LABEL_HEIGHT}px`,
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {label}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
